import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../components/Button';
import {
  COLLECT_STATUS_CONFIG,
  PARTNER_NAMES,
  PARTNER_SUPPORT_PHONES,
} from '../../constants';
import {
  buildFinalOrderItems,
  formatPhone,
  formatTenge,
  getBaseUnitPrice,
  getFinalOrderAmount,
  getProblemProducts,
} from '../../helpers';
import {
  AgreementDecision,
  AgreementDecisions,
  CollectProductData,
  CollectStatus,
  ProductMarks,
} from '../../types';
import * as Styled from './AgreementView.styles';

interface AgreementViewProps {
  products: CollectProductData[];
  marks: ProductMarks;
  decisions: AgreementDecisions;
  customerPhone: string;
  userSource: string;
  isSaving: boolean;
  onDecision: (sku: string, decision: AgreementDecision | null) => void;
  onBack: () => void;
  onSave: () => void;
}

// Доступные решения по каждому статусу проблемы
const GROUP_DECISIONS: Record<
  string,
  { decision: AgreementDecision; labelKey: string }[]
> = {
  [CollectStatus.OUT_OF_STOCK]: [
    {
      decision: AgreementDecision.REMOVE,
      labelKey: 'OrderPage.Collect.Agreement.Decision.REMOVE',
    },
  ],
  [CollectStatus.LESS]: [
    {
      decision: AgreementDecision.KEEP,
      labelKey: 'OrderPage.Collect.Agreement.Decision.KEEP_LESS',
    },
    {
      decision: AgreementDecision.REMOVE,
      labelKey: 'OrderPage.Collect.Agreement.Decision.REMOVE',
    },
  ],
  [CollectStatus.EXPIRY]: [
    {
      decision: AgreementDecision.KEEP,
      labelKey: 'OrderPage.Collect.Agreement.Decision.CLIENT_AGREED',
    },
    {
      decision: AgreementDecision.REMOVE,
      labelKey: 'OrderPage.Collect.Agreement.Decision.CLIENT_REFUSED',
    },
  ],
  [CollectStatus.DEFECT]: [
    {
      decision: AgreementDecision.REMOVE,
      labelKey: 'OrderPage.Collect.Agreement.Decision.PARTNER_NOTIFIED',
    },
  ],
  [CollectStatus.OTHER_PRICE]: [
    {
      decision: AgreementDecision.KEEP,
      labelKey: 'OrderPage.Collect.Agreement.Decision.PRICE_AGREED',
    },
    {
      decision: AgreementDecision.REMOVE,
      labelKey: 'OrderPage.Collect.Agreement.Decision.CLIENT_REFUSED',
    },
  ],
};

// Порядок отображения групп проблем
const GROUP_ORDER = [
  CollectStatus.OUT_OF_STOCK,
  CollectStatus.LESS,
  CollectStatus.EXPIRY,
  CollectStatus.DEFECT,
  CollectStatus.OTHER_PRICE,
];

const GROUP_HINT_KEYS: Record<string, string> = {
  [CollectStatus.OUT_OF_STOCK]: 'OrderPage.Collect.Agreement.Hint.OUT_OF_STOCK',
  [CollectStatus.LESS]: 'OrderPage.Collect.Agreement.Hint.LESS',
  [CollectStatus.EXPIRY]: 'OrderPage.Collect.Agreement.Hint.EXPIRY',
  [CollectStatus.DEFECT]: 'OrderPage.Collect.Agreement.Hint.DEFECT',
  [CollectStatus.OTHER_PRICE]: 'OrderPage.Collect.Agreement.Hint.OTHER_PRICE',
};

// Проблемы, требующие звонка клиенту (брак согласуется с партнёром)
const CLIENT_CALL_STATUSES = [
  CollectStatus.OUT_OF_STOCK,
  CollectStatus.LESS,
  CollectStatus.EXPIRY,
  CollectStatus.OTHER_PRICE,
];

const AgreementView = ({
  products,
  marks,
  decisions,
  customerPhone,
  userSource,
  isSaving,
  onDecision,
  onBack,
  onSave,
}: AgreementViewProps) => {
  const { t } = useTranslation();
  const [calledClient, setCalledClient] = useState<boolean>(false);
  const [calledPartner, setCalledPartner] = useState<boolean>(false);

  const problemProducts = getProblemProducts(products, marks);
  const okProducts = products.filter(
    (product) => !marks[product.sku] || !problemProducts.includes(product)
  );
  const okAmount = okProducts.reduce(
    (sum, product) => sum + product.quantity * getBaseUnitPrice(product),
    0
  );

  const decidedCount = problemProducts.filter(
    (product) => decisions[product.sku]
  ).length;
  const isAllDecided = decidedCount === problemProducts.length;

  const clientPhone = formatPhone(customerPhone);
  const partnerName = PARTNER_NAMES[userSource] || '';
  const partnerPhone = formatPhone(PARTNER_SUPPORT_PHONES[userSource]);

  const byStatus = (status: CollectStatus) =>
    problemProducts.filter((product) => marks[product.sku].status === status);

  const needClientCall = problemProducts.some((product) =>
    CLIENT_CALL_STATUSES.includes(marks[product.sku].status)
  );
  const defectProducts = byStatus(CollectStatus.DEFECT);

  const finalItems = buildFinalOrderItems(products, marks, decisions);

  const buildCallScript = (): string => {
    const parts: string[] = [];
    const outOfStock = byStatus(CollectStatus.OUT_OF_STOCK);
    if (outOfStock.length) {
      parts.push(
        t('OrderPage.Collect.Agreement.SCRIPT_OUT_OF_STOCK', {
          products: outOfStock.map((p) => `«${p.name}»`).join(', '),
        })
      );
    }
    byStatus(CollectStatus.LESS).forEach((product) => {
      parts.push(
        t('OrderPage.Collect.Agreement.SCRIPT_LESS', {
          product: `«${product.name}»`,
          quantity: marks[product.sku].quantity,
          ordered: product.quantity,
        })
      );
    });
    byStatus(CollectStatus.EXPIRY).forEach((product) => {
      parts.push(
        t('OrderPage.Collect.Agreement.SCRIPT_EXPIRY', {
          product: `«${product.name}»`,
          date: marks[product.sku].expDate,
        })
      );
    });
    byStatus(CollectStatus.OTHER_PRICE).forEach((product) => {
      parts.push(
        t('OrderPage.Collect.Agreement.SCRIPT_OTHER_PRICE', {
          product: `«${product.name}»`,
          price: formatTenge(marks[product.sku].newPrice || 0),
        })
      );
    });
    return `${t('OrderPage.Collect.Agreement.SCRIPT_GREETING')} ${parts.join(
      '. '
    )}. ${t('OrderPage.Collect.Agreement.SCRIPT_ENDING')}`;
  };

  const getMarkDetail = (product: CollectProductData): string => {
    const mark = marks[product.sku];
    switch (mark.status) {
      case CollectStatus.LESS:
        return t('OrderPage.Collect.Agreement.DETAIL_LESS', {
          quantity: mark.quantity,
          ordered: product.quantity,
        });
      case CollectStatus.EXPIRY:
        return t('OrderPage.Collect.EXP_DATE', { date: mark.expDate });
      case CollectStatus.DEFECT:
        return mark.defectReason || '';
      case CollectStatus.OTHER_PRICE:
        return t('OrderPage.Collect.PRICE_DETAIL', {
          price: formatTenge(mark.newPrice || 0),
        });
      default:
        return '';
    }
  };

  const getDecisionResultLabel = (product: CollectProductData): string => {
    if (decisions[product.sku] === AgreementDecision.REMOVE) {
      return t('OrderPage.Collect.Agreement.DecisionResult.REMOVED');
    }
    if (marks[product.sku].status === CollectStatus.LESS) {
      return t('OrderPage.Collect.Agreement.DecisionResult.KEPT_LESS', {
        quantity: marks[product.sku].quantity,
      });
    }
    return t('OrderPage.Collect.Agreement.DecisionResult.CLIENT_AGREED');
  };

  const callStepNumber = 1;
  const confirmStepNumber = needClientCall || defectProducts.length ? 2 : 1;

  return (
    <Styled.AgreementContainer>
      <div className="agreement-header">
        <span className="agreement-step">
          {t('OrderPage.Collect.Agreement.STEP')}
        </span>
        <div className="agreement-title">
          {t('OrderPage.Collect.Agreement.TITLE')} ({decidedCount}/
          {problemProducts.length})
        </div>
        <div className="agreement-subtitle">
          {t('OrderPage.Collect.Agreement.SUBTITLE')}
        </div>
      </div>

      {okProducts.length > 0 && (
        <div className="ok-summary">
          <span>
            {t('OrderPage.Collect.Agreement.OK_PRODUCTS', {
              quantity: okProducts.length,
            })}
          </span>
          <span>{formatTenge(okAmount)}</span>
        </div>
      )}

      {(needClientCall || defectProducts.length > 0) && (
        <Styled.Section>
          <div className="section-header">
            <span className="section-number">{callStepNumber}</span>
            <span className="section-title">
              {t('OrderPage.Collect.Agreement.CALL_TITLE')}
            </span>
          </div>

          {needClientCall && (
            <Styled.CallBlock done={calledClient}>
              <div className="call-row">
                <div className="call-info">
                  <div className="call-title">
                    {t('OrderPage.Collect.Agreement.CALL_CLIENT')}
                  </div>
                  <div className="call-sub">
                    {t('OrderPage.Collect.Agreement.CALL_CLIENT_SUB')}
                  </div>
                  <div className="call-phone">
                    <a href={`tel:${clientPhone}`}>{clientPhone}</a>
                  </div>
                </div>
                <button
                  type="button"
                  className={`call-button ${calledClient ? 'done' : ''}`}
                  onClick={() => setCalledClient(true)}
                  disabled={calledClient}
                >
                  {calledClient
                    ? t('OrderPage.Collect.Agreement.CALL_DONE')
                    : t('OrderPage.Collect.Agreement.CALL_BUTTON')}
                </button>
              </div>
              <div className="call-script">
                <div className="script-title">
                  {t('OrderPage.Collect.Agreement.CALL_SCRIPT_TITLE')}
                </div>
                <div className="script-text">{buildCallScript()}</div>
              </div>
            </Styled.CallBlock>
          )}

          {defectProducts.length > 0 && (
            <Styled.CallBlock done={calledPartner}>
              <div className="call-row">
                <div className="call-info">
                  <div className="call-title">
                    {t('OrderPage.Collect.Agreement.CALL_PARTNER', {
                      partner: partnerName,
                    })}
                  </div>
                  <div className="call-sub">
                    {defectProducts.map((p) => p.name).join(', ')}
                  </div>
                  {partnerPhone && (
                    <div className="call-phone">
                      <a href={`tel:${partnerPhone}`}>{partnerPhone}</a>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className={`call-button ${calledPartner ? 'done' : ''}`}
                  onClick={() => setCalledPartner(true)}
                  disabled={calledPartner}
                >
                  {calledPartner
                    ? t('OrderPage.Collect.Agreement.CALL_DONE')
                    : t('OrderPage.Collect.Agreement.CALL_BUTTON')}
                </button>
              </div>
              <div className="call-hint">
                {t('OrderPage.Collect.Agreement.DEFECT_HINT', {
                  partner: partnerName,
                })}
              </div>
            </Styled.CallBlock>
          )}
        </Styled.Section>
      )}

      <Styled.Section>
        <div className="section-header">
          <span className="section-number">{confirmStepNumber}</span>
          <span className="section-title">
            {t('OrderPage.Collect.Agreement.CONFIRM_TITLE')}
          </span>
        </div>

        {GROUP_ORDER.map((status) => {
          const groupProducts = byStatus(status);
          if (!groupProducts.length) return null;
          const config = COLLECT_STATUS_CONFIG[status];

          return (
            <Styled.ProblemGroup
              key={status}
              groupColor={config.color}
              groupBackground={config.background}
              groupBorder={config.border}
            >
              <div className="group-header">
                <span className="group-title">{t(config.fullLabelKey)}</span>
                <span className="group-count">{groupProducts.length}</span>
              </div>
              <div className="group-hint">{t(GROUP_HINT_KEYS[status])}</div>
              <div className="group-items">
                {groupProducts.map((product) => {
                  const decision = decisions[product.sku];
                  const detail = getMarkDetail(product);

                  return (
                    <Styled.ProblemItem
                      key={product.sku}
                      itemColor={config.color}
                    >
                      <div className="item-header">
                        <span
                          className={`item-name ${
                            decision === AgreementDecision.REMOVE
                              ? 'removed'
                              : ''
                          }`}
                        >
                          {product.name}
                        </span>
                        <span className="item-quantity">
                          ×{product.quantity}
                        </span>
                      </div>
                      {detail && <span className="item-detail">{detail}</span>}
                      {decision ? (
                        <div className="decision-result">
                          <span>{getDecisionResultLabel(product)}</span>
                          <button
                            type="button"
                            className="reset-button"
                            onClick={() => onDecision(product.sku, null)}
                          >
                            {t('OrderPage.Collect.Agreement.Decision.RESET')}
                          </button>
                        </div>
                      ) : (
                        <div className="decision-buttons">
                          {GROUP_DECISIONS[status].map(
                            ({ decision: value, labelKey }) => (
                              <button
                                key={value}
                                type="button"
                                className={
                                  value === AgreementDecision.KEEP
                                    ? 'keep'
                                    : 'remove'
                                }
                                onClick={() => onDecision(product.sku, value)}
                              >
                                {t(labelKey, {
                                  quantity: marks[product.sku].quantity,
                                })}
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </Styled.ProblemItem>
                  );
                })}
              </div>
            </Styled.ProblemGroup>
          );
        })}
      </Styled.Section>

      <Styled.AgreementFooter>
        <div className="footer-total">
          <span>
            {t('OrderPage.Collect.Agreement.TOTAL', {
              quantity: finalItems.length,
            })}
          </span>
          <span className="total-amount">
            {formatTenge(getFinalOrderAmount(products, marks, decisions))}
          </span>
        </div>
        {isAllDecided && finalItems.length === 0 && (
          <div className="empty-order-hint">
            {t('OrderPage.Collect.Agreement.EMPTY_ORDER_HINT')}
          </div>
        )}
        <div className="footer-actions">
          <Button
            text={t('OrderPage.Collect.Agreement.BACK')}
            variant="outlined"
            onClick={onBack}
          />
          <Button
            text={t('OrderPage.Collect.Agreement.SAVE')}
            variant="contained"
            disabled={!isAllDecided || isSaving || finalItems.length === 0}
            isLoading={isSaving}
            onClick={onSave}
          />
        </div>
      </Styled.AgreementFooter>
    </Styled.AgreementContainer>
  );
};

export default AgreementView;
